import type {
	GroupRegistryContext,
	SuggestionGroupEntry,
	SuggestionGroupKey,
	SuggestProviderKey
} from './types';
import type { Suggestion } from '~/components/SearchSuggest/types';

import IconMaterialSymbolsLightCorporateFare from '~icons/material-symbols-light/corporate-fare';
import IconMaterialSymbolsLightExploreOutline from '~icons/material-symbols-light/explore-outline';
import IconMaterialSymbolsLightHistory from '~icons/material-symbols-light/history';
import IconMaterialSymbolsLightHomeWorkOutline from '~icons/material-symbols-light/home-work-outline';
import IconMaterialSymbolsLightMapOutline from '~icons/material-symbols-light/map-outline';
import IconMaterialSymbolsLightWhereToVoteOutline from '~icons/material-symbols-light/where-to-vote-outline';

/**
 * Map API keys to registry keys for provider matching
 */
const PROVIDER_TO_REGISTRY_KEY: Record<SuggestProviderKey, SuggestionGroupKey> =
	{
		cities: 'cities',
		macroregions: 'macroregions',
		regions: 'regions',
		streets: 'streets',
		points_of_interest: 'pointsOfInterest',
		condos: 'condos'
	};

/**
 * Strategy registry for suggestion groups
 * Provides visibility rules, item transforms, and component mapping
 *
 * @param providers - Array of provider keys to enable
 * @returns Filtered group entries based on providers
 */
export function useGroupRegistry(providers: SuggestProviderKey[] = []) {
	/**
	 * Get the registry keys that match the enabled providers
	 */
	const enabledGroupKeys = new Set(
		providers.map(p => PROVIDER_TO_REGISTRY_KEY[p]).filter(Boolean)
	);

	/**
	 * Base registry with all available groups
	 */
	const baseRegistry: Record<SuggestionGroupKey, SuggestionGroupEntry> = {
		lastResearchs: {
			key: 'lastResearchs',
			heading: {
				text: 'Pesquisas recentes',
				icon: IconMaterialSymbolsLightHistory
			},
			shouldShow: ({ state }) => {
				// Check if there are location results that match THIS field's providers
				const hasRelevantLocationResults =
					state.locationAutocompleteData.value &&
					state.locationAutocompleteData.value.length > 0 &&
					state.locationAutocompleteData.value.some(
						group =>
							// Only check groups that are enabled for this field
							enabledGroupKeys.has(group.key as SuggestionGroupKey) &&
							group.suggestions &&
							group.suggestions.length > 0
					);

				return (
					!state.disabled?.value &&
					state.filteredLastResearchs.value.length > 0 &&
					!state.loading.value &&
					!hasRelevantLocationResults
				);
			},
			getItems: ({ state }) => state.filteredLastResearchs.value,
			component: 'NewGroup',
			isRemovable: true
		},

		regions: {
			key: 'regions',
			heading: {
				text: 'Bairros',
				icon: IconMaterialSymbolsLightHomeWorkOutline
			},
			shouldShow: ({ state, hasMinSearchTerm }) => {
				const regionsData =
					state.locationAutocompleteData.value?.find(g => g.key === 'regions')
						?.suggestions || [];
				return (
					!state.disabled?.value &&
					hasMinSearchTerm &&
					regionsData.length > 0 &&
					!state.loading.value
				);
			},
			getItems: ({ state }) => {
				return (state.locationAutocompleteData.value?.find(
					g => g.key === 'regions'
				)?.suggestions || []) as Suggestion[];
			},
			component: 'NewGroup'
		},

		cities: {
			key: 'cities',
			heading: {
				text: 'Cidades',
				icon: IconMaterialSymbolsLightMapOutline
			},
			shouldShow: ({ state, hasMinSearchTerm }) => {
				const citiesData =
					state.locationAutocompleteData.value?.find(g => g.key === 'cities')
						?.suggestions || [];
				return (
					!state.disabled?.value &&
					hasMinSearchTerm &&
					citiesData.length > 0 &&
					!state.loading.value
				);
			},
			getItems: ({ state }) => {
				return (state.locationAutocompleteData.value?.find(
					g => g.key === 'cities'
				)?.suggestions || []) as Suggestion[];
			},
			component: 'NewGroup'
		},

		macroregions: {
			key: 'macroregions',
			heading: {
				text: 'Regiões',
				icon: IconMaterialSymbolsLightExploreOutline
			},
			shouldShow: ({ state, hasMinSearchTerm }) => {
				const macroregionsData = (state.locationAutocompleteData.value?.find(
					g => g.key === 'macroregions'
				)?.suggestions || []) as Suggestion[];
				const availableMacroregions = macroregionsData.filter(
					m => m.available !== false
				);
				return (
					!state.disabled?.value &&
					hasMinSearchTerm &&
					availableMacroregions.length > 0 &&
					!state.loading.value
				);
			},
			getItems: ({ state }) => {
				const allMacroregions = (state.locationAutocompleteData.value?.find(
					g => g.key === 'macroregions'
				)?.suggestions || []) as Suggestion[];

				return allMacroregions.filter(m => m.available !== false);
			},
			component: 'NewGroup'
		},

		streets: {
			key: 'streets',
			heading: {
				text: 'Endereços',
				icon: IconMaterialSymbolsLightMapOutline
			},
			shouldShow: ({ state, hasMinSearchTerm }) => {
				const streetsData =
					state.locationAutocompleteData.value?.find(g => g.key === 'streets')
						?.suggestions || [];
				return (
					!state.disabled?.value &&
					hasMinSearchTerm &&
					streetsData.length > 0 &&
					!state.loading.value
				);
			},
			getItems: ({ state }) => {
				return (state.locationAutocompleteData.value?.find(
					g => g.key === 'streets'
				)?.suggestions || []) as Suggestion[];
			},
			component: 'NewGroup'
		},

		pointsOfInterest: {
			key: 'pointsOfInterest',
			heading: {
				text: 'Pontos de interesse',
				icon: IconMaterialSymbolsLightWhereToVoteOutline
			},
			shouldShow: ({ state, hasMinSearchTerm }) => {
				const poisData =
					state.locationAutocompleteData.value?.find(
						g => g.key === 'pointsOfInterest'
					)?.suggestions || [];
				return (
					!state.disabled?.value &&
					hasMinSearchTerm &&
					poisData.length > 0 &&
					!state.loading.value
				);
			},
			getItems: ({ state }) => {
				return (state.locationAutocompleteData.value?.find(
					g => g.key === 'pointsOfInterest'
				)?.suggestions || []) as Suggestion[];
			},
			component: 'NewGroup'
		},

		condos: {
			key: 'condos',
			heading: {
				text: 'Condomínios',
				icon: IconMaterialSymbolsLightCorporateFare
			},
			shouldShow: ({ state, hasMinSearchTerm }) => {
				const condosData =
					state.locationAutocompleteData.value?.find(g => g.key === 'condos')
						?.suggestions || [];
				return (
					!state.disabled?.value &&
					hasMinSearchTerm &&
					condosData.length > 0 &&
					!state.loading.value
				);
			},
			getItems: ({ state }) => {
				return (state.locationAutocompleteData.value?.find(
					g => g.key === 'condos'
				)?.suggestions || []) as Suggestion[];
			},
			component: 'NewGroup'
		},

		code: {
			key: 'code',
			heading: {
				text: 'Código do imóvel',
				icon: null as any
			},
			shouldShow: ({ state }) => {
				return (
					!state.disabled?.value &&
					state.searchByCodeOption.value.length > 0 &&
					!state.loading.value
				);
			},
			getItems: ({ state }) => state.searchByCodeOption.value,
			component: 'CodeGroup'
		}
	};

	/**
	 * Filter groups based on enabled providers
	 * Always include history and code groups
	 */
	function getFilteredGroups(
		groupOrder?: SuggestionGroupKey[]
	): SuggestionGroupEntry[] {
		// Always include these groups
		const alwaysInclude: SuggestionGroupKey[] = ['lastResearchs', 'code'];

		// Map provider keys to registry keys
		const registryKeys = providers
			.map(p => PROVIDER_TO_REGISTRY_KEY[p])
			.filter(Boolean);

		// Determine which groups to include
		const enabledKeys = new Set<SuggestionGroupKey>([
			...alwaysInclude,
			...registryKeys
		]);

		// Get entries for enabled groups
		const entries = Array.from(enabledKeys)
			.map(key => baseRegistry[key])
			.filter(Boolean);

		// Apply custom order if provided
		if (groupOrder && groupOrder.length > 0) {
			const orderMap = new Map(groupOrder.map((key, idx) => [key, idx]));
			return entries.sort((a, b) => {
				const aOrder = orderMap.get(a.key) ?? Number.MAX_SAFE_INTEGER;
				const bOrder = orderMap.get(b.key) ?? Number.MAX_SAFE_INTEGER;
				return aOrder - bOrder;
			});
		}

		return entries;
	}

	/**
	 * Get visible groups with items for current context
	 */
	function getVisibleGroups(
		context: GroupRegistryContext,
		groupOrder?: SuggestionGroupKey[]
	): Array<SuggestionGroupEntry & { suggestions: Suggestion[] }> {
		const filteredGroups = getFilteredGroups(groupOrder);

		return filteredGroups
			.filter(group => group.shouldShow(context))
			.map(group => ({
				...group,
				suggestions: group.getItems(context)
			}))
			.filter(group => group.suggestions.length > 0);
	}

	return {
		baseRegistry,
		getFilteredGroups,
		getVisibleGroups
	};
}

/**
 * Field-specific presets
 */
export const FIRST_FIELD_PROVIDERS: SuggestProviderKey[] = [
	'cities',
	'macroregions'
];

export const SECOND_FIELD_PROVIDERS: SuggestProviderKey[] = [
	'regions',
	'streets',
	'points_of_interest',
	'condos'
];

// Group key mapping for API keys to registry keys
export const API_KEY_TO_REGISTRY_KEY: Record<string, SuggestionGroupKey> = {
	points_of_interest: 'pointsOfInterest',
	lastResearchs: 'lastResearchs',
	regions: 'regions',
	cities: 'cities',
	macroregions: 'macroregions',
	streets: 'streets',
	condos: 'condos',
	code: 'code'
};

/**
 * Default group order for first field
 */
export const FIRST_FIELD_GROUP_ORDER: SuggestionGroupKey[] = [
	'lastResearchs',
	'cities',
	'macroregions',
	'code'
];

/**
 * Default group order for second field
 */
export const SECOND_FIELD_GROUP_ORDER: SuggestionGroupKey[] = [
	'lastResearchs',
	'regions',
	'streets',
	'pointsOfInterest',
	'condos',
	'code'
];
